import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import CustomStore from "devextreme/data/custom_store";
import saveAs from "file-saver";
import "jspdf-autotable";
import { jsPDF } from "jspdf";
import { DateBox, LoadPanel, Popup } from "devextreme-react";
import DataGrid, {
  Column,
  ColumnChooser,
  FilterRow,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Item,
  LoadPanel as GridLoadPanel,
  Pager,
  Paging,
  SearchPanel,
  Selection,
  Summary,
  Toolbar,
  TotalItem,
} from "devextreme-react/data-grid";
import { useNavigate } from "react-router-dom";

import { DynamicTabb } from "../../../components/DynamicTab/DynamicTab";
import { Toster } from "../../../components/Toster/Toster";
import { logOut } from "../../../components/Logout";
import { handleGetLocal } from "../../../services/auth-service";

import "./BalanceReport.css";

const pageSizes = [25, 50, 100, 250];
const balanceRemoteOperations = {
  paging: true,
  sorting: true,
  filtering: true,
};

const exportColumns = [
  { field: "offenderNumber", caption: "Offender #" },
  { field: "lastFirst", caption: "Name" },
  { field: "agency", caption: "Agency" },
  { field: "company", caption: "Company" },
  { field: "region", caption: "Region" },
  { field: "bIsBilling", caption: "Billing" },
  { field: "currentBalance", caption: "Current Balance", currency: true },
  { field: "balanceAsOf", caption: "Balance As Of", currency: true },
];

const formatApiDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const BalanceReport = () => {
  const navigate = useNavigate();

  const gridRef = useRef(null);
  const headerFilterOpenRef = useRef(false);
  const mainLoadCountRef = useRef(0);
  const reportDateRef = useRef(new Date());

  const pathname = handleGetLocal("pathURL");

  const [asOfDate, setAsOfDate] = useState(new Date());
  const [reportDate, setReportDate] = useState(new Date());

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [gridLoading, setGridLoading] = useState(true);
  const [exportingFormat, setExportingFormat] = useState(null);

  const [toast, setToast] = useState({
    isVisible: false,
    type: "",
    message: "",
  });

  const showToast = (message, type = "error") => {
    setToast({
      isVisible: true,
      type,
      message,
    });

    setTimeout(() => {
      setToast({
        isVisible: false,
        type: "",
        message: "",
      });
    }, 2500);
  };

  const handleGridInitialized = useCallback((e) => {
    const dataSource = e.component.getDataSource();

    if (!dataSource) {
      return;
    }

    const handleLoadingChanged = (isLoading) => {
      if (headerFilterOpenRef.current) {
        setGridLoading(false);
        return;
      }
      setGridLoading(isLoading);
    };

    dataSource.on("loadingChanged", handleLoadingChanged);
  }, []);

  useEffect(() => {
    const handleDocumentMouseDown = (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const headerFilterIcon = target.closest(".dx-header-filter, .dx-header-filter-indicator");

      if (headerFilterIcon) {
        headerFilterOpenRef.current = true;
        setGridLoading(false);

        return;
      }

      const headerFilterPopup = target.closest(".dx-header-filter-menu");

      if (headerFilterPopup) {
        const button = target.closest(".dx-button");

        if (button) {
          const buttonText = button.querySelector(".dx-button-text")?.textContent?.trim()?.toLowerCase() || "";
          if (buttonText === "ok") {
            headerFilterOpenRef.current = false;
            setGridLoading(true);

            return;
          }
          if (buttonText === "cancel") {
            headerFilterOpenRef.current = false;
            setGridLoading(false);

            return;
          }
        }

        // Checkbox/value click.
        // Keep header filter mode active.
        return;
      }

      headerFilterOpenRef.current = false;
    };

    document.addEventListener("mousedown", handleDocumentMouseDown, true);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown, true);
    };
  }, []);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    Domain: `${pathname}`,
  });

  const handleError = (error, fallback) => {
    console.error(fallback, error);

    if (!error?.response) {
      navigate("/error/500");
    } else if (error.response.status === 401) {
      logOut();
    } else {
      showToast(typeof error.response.data === "string" ? error.response.data : fallback);
    }
  };

  const getHeaderFilterDataSource = (field) => ({
    load: async (loadOptions) => {
      const params = {
        field,
      };

      if (
        loadOptions?.searchValue !== null &&
        loadOptions?.searchValue !== undefined &&
        String(loadOptions.searchValue).trim() !== ""
      ) {
        params.search = String(loadOptions.searchValue).trim();
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/report-center/balance/header-filter`, {
        params,
        headers: headers(),
      });

      const values = Array.isArray(response?.data) ? response.data : [];

      return values.map((value) => ({
        text: value,
        value,
      }));
    },
  });

  const extractBalanceFilters = (filter) => {
    const result = {
      offenderNumber: null,
      lastFirst: null,
      agency: [],
      company: [],
      region: [],
      billing: [],
      search: null,
    };

    if (!filter) {
      return result;
    }

    const walk = (expression) => {
      if (!Array.isArray(expression)) {
        return;
      }

      if (expression.length === 3 && typeof expression[0] === "string") {
        const [field, operation, value] = expression;

        if (operation === "=") {
          if (field === "offenderNumber") {
            result.offenderNumber = value;
          }

          if (field === "lastFirst") {
            result.lastFirst = value;
          }

          if (field === "agency") {
            result.agency.push(value);
          }

          if (field === "company") {
            result.company.push(value);
          }

          if (field === "region") {
            result.region.push(value);
          }

          if (field === "bIsBilling") {
            result.billing.push(value);
          }
        }

        if (
          operation === "contains" &&
          ["offenderNumber", "lastFirst", "agency", "company", "region"].includes(field)
        ) {
          if (value !== undefined && value !== null) {
            result.search = String(value).trim();
          }
        }

        return;
      }

      expression.forEach((item) => {
        if (Array.isArray(item)) {
          walk(item);
        }
      });
    };

    walk(filter);

    return result;
  };

  const handleGridLoadingChanged = useCallback((isLoading) => {
    // Header filter has its OWN spinner.
    // Never show our big spinner while header-filter popup is open.
    if (headerFilterOpenRef.current) {
      setGridLoading(false);
      return;
    }

    setGridLoading(isLoading);
  }, []);
  const handleGridCellClick = useCallback((e) => {
    const nativeEvent = e?.event;

    if (!nativeEvent?.target) {
      return;
    }

    const headerFilterIcon = nativeEvent.target.closest(".dx-header-filter, .dx-header-filter-indicator");

    if (headerFilterIcon) {
      headerFilterOpenRef.current = true;

      // Kill the big spinner immediately.
      setGridLoading(false);
    }
  }, []);
  const handleGridContentReady = useCallback(() => {
    const popup = document.querySelector(".dx-header-filter-menu.dx-popup-wrapper");

    if (!popup) {
      headerFilterOpenRef.current = false;
    }
  }, []);

  const balanceStore = useMemo(
    () =>
      new CustomStore({
        key: "rowNumber",

        load: async (loadOptions) => {
          const extractedFilters = extractBalanceFilters(loadOptions.filter);

          const params = {
            asOfDate: formatApiDate(reportDateRef.current),
            skip: loadOptions.skip ?? 0,
            take: loadOptions.take ?? 25,
          };

          if (extractedFilters.offenderNumber) {
            params.offenderNumber = extractedFilters.offenderNumber;
          }

          if (extractedFilters.lastFirst) {
            params.lastFirst = extractedFilters.lastFirst;
          }

          if (extractedFilters.agency.length > 0) {
            params.agency = extractedFilters.agency.join(",");
          }

          if (extractedFilters.company.length > 0) {
            params.company = extractedFilters.company.join(",");
          }

          if (extractedFilters.region.length > 0) {
            params.region = extractedFilters.region.join(",");
          }

          if (extractedFilters.billing.length === 1) {
            params.billing = extractedFilters.billing[0];
          }

          if (extractedFilters.search) {
            params.search = extractedFilters.search;
          }

          try {
            setGridLoading(true);

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/report-center/balance`, {
              params,
              headers: headers(),
            });

            return {
              data: Array.isArray(response?.data?.data) ? response.data.data : [],
              totalCount: Number(response?.data?.totalCount ?? 0),
            };
          } catch (error) {
            handleError(error, "Unable to load the Balance Report.");

            throw error;
          } finally {
            setGridLoading(false);
          }
        },
      }),
    [pathname],
  );
  const refreshReport = () => {
    if (!asOfDate) {
      showToast("Select an as-of date.");
      return;
    }

    const selectedDate = new Date(asOfDate);

    reportDateRef.current = selectedDate;
    setReportDate(selectedDate);

    gridRef.current?.instance()?.refresh();
  };
  const clearFilters = () => {
    const grid = gridRef.current?.instance();

    if (!grid) {
      return;
    }

    grid.clearFilter();
    grid.clearGrouping();
    grid.clearSelection();
  };

  const exportValue = (row, column) => {
    if (column.field === "bIsBilling") {
      return row.bIsBilling === true ? "Yes" : row.bIsBilling === false ? "No" : "Unknown";
    }

    return row[column.field] ?? "";
  };

  const getCurrentExportParams = () => {
    const grid = gridRef.current?.instance();

    const params = {
      asOfDate: formatApiDate(reportDate),
    };

    if (!grid) {
      return params;
    }

    const filter = grid.getCombinedFilter(true);
    const extractedFilters = extractBalanceFilters(filter);

    if (extractedFilters.offenderNumber) {
      params.offenderNumber = extractedFilters.offenderNumber;
    }

    if (extractedFilters.lastFirst) {
      params.lastFirst = extractedFilters.lastFirst;
    }

    if (extractedFilters.agency.length > 0) {
      params.agency = extractedFilters.agency.join(",");
    }

    if (extractedFilters.company.length > 0) {
      params.company = extractedFilters.company.join(",");
    }

    if (extractedFilters.region.length > 0) {
      params.region = extractedFilters.region.join(",");
    }

    if (extractedFilters.billing.length === 1) {
  params.billing = extractedFilters.billing[0];
}

    if (extractedFilters.search) {
      params.search = extractedFilters.search;
    }

    return params;
  };

  const loadAllRows = () => {
    const batchSize = 500;
    const rows = [];

    const exportParams = getCurrentExportParams();

    const loadBatch = (skip = 0) => {
      return axios
        .get(`${process.env.REACT_APP_API_URL}/api/report-center/balance`, {
          params: {
            ...exportParams,
            skip,
            take: batchSize,
          },
          headers: headers(),
        })
        .then((response) => {
          const page = Array.isArray(response?.data?.data) ? response.data.data : [];

          const totalCount = Number(response?.data?.totalCount ?? 0);

          rows.push(...page);

          const nextSkip = skip + page.length;

          if (page.length > 0 && nextSkip < totalCount) {
            return loadBatch(nextSkip);
          }

          return rows;
        });
    };

    return loadBatch();
  };

  const runExport = async (format) => {
    if (exportingFormat) {
      return;
    }

    setExportingFormat(format);

    try {
      const dateText = formatApiDate(reportDate);

      const params = {
        ...getCurrentExportParams(),
        format,
      };

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/report-center/balance/export`, {
        params,
        headers: headers(),
        responseType: "blob",
      });

      const extensions = {
        excel: "xlsx",
        csv: "csv",
        pdf: "pdf",
        word: "docx",
      };

      const contentTypes = {
        excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        csv: "text/csv;charset=utf-8",
        pdf: "application/pdf",
        word: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      };

      saveAs(
        new Blob([response.data], {
          type: contentTypes[format],
        }),
        `Balance_Report_${dateText}.${extensions[format]}`,
      );

      showToast(`${format.toUpperCase()} export completed.`, "success");
    } catch (error) {
      let message = `Unable to export the Balance Report as ${format}.`;

      if (error?.response?.data instanceof Blob) {
        const text = await error.response.data.text();

        if (text) {
          message = text;
        }
      }

      if (error?.response?.status === 400) {
        showToast(message, "error");
        return;
      }

      handleError(error, message);
    } finally {
      setExportingFormat(null);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedRow(null);
    setDetailRows([]);
  };

  const loadDetail = (row) => {
    setSelectedRow(row);
    setDetailRows([]);
    setDetailOpen(true);
    setDetailLoading(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/report-center/balance/detail`, {
        params: {
          asOfDate: formatApiDate(reportDate),
          personId: row.personId,
        },
        headers: headers(),
      })
      .then((response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];

        setDetailRows(
          rows.map((detail, index) => ({
            ...detail,

            detailRowId: `${detail.obligationId || "detail"}-${detail.transactionType || ""}-${index}`,
          })),
        );
      })
      .catch((error) => {
        closeDetail();

        handleError(error, "Unable to load balance details.");
      })
      .finally(() => {
        setDetailLoading(false);
      });
  };

  const handleBalanceRowClick = useCallback(
    (e) => {
      if (e?.rowType === "data" && e?.data?.personId) {
        loadDetail(e.data);
      }
    },
    [reportDate],
  );

  return (
    <>
      <Toster message={toast.message} type={toast.type} visible={toast.isVisible} />

      <Popup
        visible={detailOpen}
        onHiding={closeDetail}
        title={`Balance Detail${selectedRow?.lastFirst ? ` — ${selectedRow.lastFirst}` : ""}`}
        showCloseButton={true}
        hideOnOutsideClick={false}
        width="82%"
        maxWidth={1150}
        height="82%"
        wrapperAttr={{
          class: "balance-detail-popup-wrapper",
        }}
      >
        <div className="balance-detail-grid">
          <LoadPanel
            shadingColor="rgba(0,0,0,0.08)"
            visible={detailLoading}
            delay={10}
            container=".balance-detail-grid"
          />

          <DataGrid
            dataSource={detailRows}
            keyExpr="detailRowId"
            showBorders={false}
            showRowLines={true}
            showColumnLines={false}
            rowAlternationEnabled={false}
            columnAutoWidth={true}
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnResizingMode="widget"
            hoverStateEnabled={true}
            height="100%"
            noDataText="No balance details found."
          >
            <SearchPanel visible={true} width={220} placeholder="Search detail..." highlightSearchText={true} />

            <HeaderFilter
              visible={true}
              search={{
                enabled: true,
              }}
            />

            <FilterRow visible={false} />

            <Column dataField="transactionDate" caption="Date" dataType="date" format="MMM d, yyyy" />

            <Column dataField="transactionType" caption="Transaction" />

            <Column dataField="chargeType" caption="Charge Type" />

            <Column dataField="equipmentTypeName" caption="Equipment" minWidth={180} />

            <Column
              dataField="amount"
              caption="Amount"
              dataType="number"
              format={{
                type: "currency",
                precision: 2,
              }}
            />

            <Column dataField="rateStart" caption="Rate Start" dataType="date" format="MMM d, yyyy" />

            <Column dataField="rateEnd" caption="Rate End" dataType="date" format="MMM d, yyyy" />

            <Column dataField="daysActive" caption="Days" dataType="number" />

            <Column
              dataField="rate"
              caption="Rate"
              dataType="number"
              format={{
                type: "currency",
                precision: 2,
              }}
            />

            <Summary>
              <TotalItem
                column="amount"
                summaryType="sum"
                valueFormat={{
                  type: "currency",
                  precision: 2,
                }}
                displayFormat="Total: {0}"
              />
            </Summary>

            <Paging defaultPageSize={25} />

            <Pager
              visible={true}
              allowedPageSizes={[25, 50, 100]}
              showPageSizeSelector={true}
              showNavigationButtons={true}
              showInfo={true}
            />
          </DataGrid>
        </div>
      </Popup>

      <div className="page-section balance-report-page">
        <DynamicTabb />

        <div className="page-content balance-modern-content">
          <div className="screen_num">
            <p>FN.16</p>
          </div>

          {/* BALANCE DATE CARD */}
          <div className="balance-date-card">
            <div className="balance-date-heading">
              <div className="balance-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 4H18V2H16V4H8V2H6V4H5C3.9 4 3 4.9 3 6V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z"
                    fill="currentColor"
                  />
                </svg>
              </div>

              <div>
                <h2>Balance As Of</h2>

                <p>Select the date used to calculate each person&apos;s balance.</p>
              </div>
            </div>

            <div className="balance-date-controls">
              <DateBox
                type="date"
                value={asOfDate}
                displayFormat="MMM d, yyyy"
                onValueChanged={(e) => setAsOfDate(e.value)}
              />

              <button type="button" className="balance-primary-button" onClick={refreshReport}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M17.65 6.35C16.02 4.72 13.71 3.78 11.17 4.04C7.5 4.41 4.48 7.39 4.07 11.06C3.52 15.91 7.27 20 12 20C15.19 20 17.93 18.13 19.21 15.44C19.53 14.77 19.05 14 18.31 14C17.94 14 17.59 14.2 17.43 14.53C16.3 16.96 13.59 18.5 10.63 17.84C8.41 17.35 6.62 15.54 6.15 13.32C5.31 9.44 8.26 6 12 6C13.66 6 15.14 6.69 16.22 7.78L14.71 9.29C14.08 9.92 14.52 11 15.41 11H19C19.55 11 20 10.55 20 10V6.41C20 5.52 18.92 5.07 18.29 5.7L17.65 6.35Z"
                    fill="currentColor"
                  />
                </svg>
                Refresh
              </button>
            </div>
          </div>
          <div className="balance-grid-card">
            <LoadPanel
              visible={gridLoading && !headerFilterOpenRef.current}
              showIndicator={true}
              showPane={true}
              shading={false}
              message="Loading..."
              container=".balance-grid-card"
            />
            <div className="balance-grid-header">
              <div className="balance-grid-heading">
                <div className="balance-section-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path
                      d="M3 3H21V21H3V3ZM5 5V8H19V5H5ZM5 10V19H9V10H5ZM11 10V19H15V10H11ZM17 10V19H19V10H17Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>

                <div>
                  <h2>Balance Summary</h2>

                  <p>Results load one page at a time. Click a row to view its transactions.</p>
                </div>
              </div>

              <div className="balance-grid-actions">
                <button type="button" className="balance-secondary-button" onClick={clearFilters}>
                  <span className="balance-clear-icon">×</span>
                  Clear Filters
                </button>

                <button
                  type="button"
                  className="balance-secondary-button"
                  disabled={Boolean(exportingFormat)}
                  onClick={() => runExport("excel")}
                >
                  <span className="balance-export-icon balance-excel-icon">X</span>

                  {exportingFormat === "excel" ? "Preparing..." : "Excel"}
                </button>

                <button
                  type="button"
                  className="balance-secondary-button"
                  disabled={Boolean(exportingFormat)}
                  onClick={() => runExport("pdf")}
                >
                  <span className="balance-export-icon balance-pdf-icon">PDF</span>

                  {exportingFormat === "pdf" ? "Preparing..." : "PDF"}
                </button>

                <button
                  type="button"
                  className="balance-secondary-button"
                  disabled={Boolean(exportingFormat)}
                  onClick={() => runExport("csv")}
                >
                  <span className="balance-export-icon balance-csv-icon">CSV</span>

                  {exportingFormat === "csv" ? "Preparing..." : "CSV"}
                </button>

                <button
                  type="button"
                  className="balance-secondary-button"
                  disabled={Boolean(exportingFormat)}
                  onClick={() => runExport("word")}
                >
                  <span className="balance-export-icon balance-word-icon">W</span>

                  {exportingFormat === "word" ? "Preparing..." : "Word"}
                </button>
              </div>
            </div>

            <DataGrid
              ref={gridRef}
              dataSource={balanceStore}
              keyExpr="rowNumber"
              remoteOperations={balanceRemoteOperations}
              onRowClick={handleBalanceRowClick}
              onCellClick={handleGridCellClick}
              onInitialized={handleGridInitialized}
              showBorders={false}
              showRowLines={true}
              showColumnLines={false}
              allowColumnReordering={true}
              allowColumnResizing={true}
              columnResizingMode="widget"
              columnAutoWidth={true}
              hoverStateEnabled={true}
              width="100%"
              height={620}
              noDataText="No balances found for the selected date."
            >
              <GridLoadPanel enabled={false} />
              <Toolbar>
                <Item name="groupPanel" location="before" />

                <Item name="searchPanel" location="after" />

                <Item name="columnChooserButton" location="after" />
              </Toolbar>

              <GroupPanel visible={true} emptyPanelText="Drag a column header here to group" />

              <Grouping autoExpandAll={false} contextMenuEnabled={true} />

              <SearchPanel visible={true} width={245} placeholder="Search this page..." highlightSearchText={true} />

              <FilterRow visible={false} />

              <HeaderFilter
                visible={true}
                search={{
                  enabled: true,
                }}
              />

              <ColumnChooser height="340px" width="400px" enabled={true} mode="dragAndDrop" />

              <Selection mode="single" />

              <Column dataField="offenderNumber" caption="Offender #" minWidth={115} />

              <Column dataField="lastFirst" caption="Name" minWidth={190} />

              <Column
                dataField="agency"
                caption="Agency"
                minWidth={190}
                headerFilter={{
                  dataSource: getHeaderFilterDataSource("agency"),
                }}
              />

              <Column
                dataField="company"
                caption="Company"
                minWidth={170}
                headerFilter={{
                  dataSource: getHeaderFilterDataSource("company"),
                }}
              />

              <Column
                dataField="region"
                caption="Region"
                minWidth={140}
                headerFilter={{
                  dataSource: getHeaderFilterDataSource("region"),
                }}
              />

              <Column
                dataField="bIsBilling"
                caption="Billing"
                width={100}
                dataType="boolean"
                customizeText={(cellInfo) => {
                  if (cellInfo.value === true) return "Yes";
                  if (cellInfo.value === false) return "No";
                  return "Unknown";
                }}
                headerFilter={{
                  dataSource: [
                    { text: "Yes", value: true },
                    { text: "No", value: false },
                  ],
                }}
              />
              <Column
                dataField="currentBalance"
                caption="Current Balance"
                dataType="number"
                alignment="left"
                minWidth={145}
                format={{
                  type: "currency",
                  precision: 2,
                }}
              />

              <Column
                dataField="balanceAsOf"
                caption="Balance As Of"
                dataType="number"
                alignment="left"
                minWidth={145}
                format={{
                  type: "currency",
                  precision: 2,
                }}
              />

              <Column dataField="personId" visible={false} showInColumnChooser={false} />

              <Column dataField="rowNumber" visible={false} showInColumnChooser={false} />

              <Paging defaultPageSize={25} />

              <Pager
                visible={true}
                allowedPageSizes={pageSizes}
                showPageSizeSelector={true}
                showNavigationButtons={true}
                showInfo={true}
                infoText="Page {0} of {1} ({2} records)"
              />
            </DataGrid>
          </div>
        </div>
      </div>
    </>
  );
};

export default BalanceReport;
